
import { useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import config from '../utils/config';
import { setupAuthGuardTokenInterceptor } from '../utils/apiInterceptor';

// Create a dedicated API client for semantic analysis
const semanticApi = axios.create({
  baseURL: config.api.module1, // Semantic API uses module1 endpoint
});

// Attach AuthGuard token from localStorage to all semantic API requests
setupAuthGuardTokenInterceptor(semanticApi);

// Interface for semantic analysis request
export interface SemanticAnalysisRequest {
  workflow_id: string;
  client_id: string;
  relevant_keywords_blob: string;
  topic_framework_blob: string;
  undecided_keywords_blob?: string;
  suggested_categories?: any[];
  forceReanalysis?: boolean;  // Using camelCase to match existing frontend conventions
  custom_prompt?: string;  // Optional custom prompt to override default
}

// Interface for semantic analysis status
export interface SemanticAnalysisStatus {
  analysis_id?: string;
  workflow_id: string;
  client_id?: string;
  status: 'queued' | 'processing' | 'completed' | 'failed' | 'not_found';
  created_at?: string;
  updated_at?: string;
  message?: string;
  framework_id?: string;
  output_blob?: string;
  semantic_framework?: SemanticFramework;
}

// Interface for classification framework topic
export interface FrameworkTopic {
  topic_id: number;
  name: string;
  definition: string;
  examples: string[];
}

// Interface for entity metadata
export interface EntityMetadata {
  detected_locations: string[];
  detected_organizations: string[];
  detected_products: string[];
}

// Interface for semantic framework
export interface SemanticFramework {
  framework_id: string;
  workflow_id: string;
  client_id: string;
  categories: {
    name: string;
    definition: string;
    examples: string[];
    subtopics: {
      name: string;
      definition: string;
      examples: string[];
    }[];
    source_module: string;
    type: string;
    patterns?: {
      pattern: string;
      description: string;
      examples: string[];
    }[];
  }[];
  created_at: string;
  version: number;
  is_finalized: boolean;
  polishing_metadata?: {
    polished_at: string;
    polisher_version: string;
    improvements_applied: any;
  };
}

// Interface for Module4 export request
export interface ExportModule4Request {
  framework_id: string;
  client_id: string;
  workflow_id: string;
  config?: {
    config_id?: string;
    name?: string;
    description?: string;
  };
}

// Service implementation
export class SemanticService {
  private static instance: SemanticService;

  private constructor() {}

  static getInstance(): SemanticService {
    if (!SemanticService.instance) {
      SemanticService.instance = new SemanticService();
    }
    return SemanticService.instance;
  }

  // Helper method to get Application Insights
  private getAppInsights() {
    return (window as any).__appInsights || null;
  }

  // Start semantic analysis
  public async startAnalysis(request: SemanticAnalysisRequest): Promise<SemanticAnalysisStatus> {
    console.log('Starting semantic analysis:', request);
    
    // Track API call start in Application Insights
    const appInsights = this.getAppInsights();
    const startTime = Date.now();
    const correlationId = `semantic-analysis-${request.workflow_id}-${Date.now()}`;
    
    if (appInsights) {
      appInsights.trackEvent({
        name: 'SemanticAPI_AnalysisStarted',
        properties: {
          correlationId,
          workflowId: request.workflow_id,
          clientId: request.client_id,
          operation: 'startAnalysis',
          timestamp: new Date().toISOString()
        }
      });
    }
    
    try {
      // API call to start analysis
      const response = await semanticApi.post('/api/v1/semantic/analyze', {
        workflow_id: request.workflow_id,
        client_id: request.client_id,
        relevant_keywords_blob: request.relevant_keywords_blob,
        topic_framework_blob: request.topic_framework_blob,
        undecided_keywords_blob: request.undecided_keywords_blob,
        suggested_categories: request.suggested_categories || [],
        force_reanalysis: request.forceReanalysis || false  // Convert camelCase to snake_case for API
      });
      
      // Track successful API call in Application Insights
      if (appInsights) {
        const duration = Date.now() - startTime;
        appInsights.trackEvent({
          name: 'SemanticAPI_AnalysisRequestComplete',
          properties: {
            correlationId,
            workflowId: request.workflow_id,
            clientId: request.client_id,
            operation: 'startAnalysis',
            responseStatus: response.data?.status || 'unknown',
            durationMs: duration,
            timestamp: new Date().toISOString()
          }
        });
      }
      
      return response.data;
    } catch (error) {
      console.error('Error starting semantic analysis:', error);
      
      // Track API error in Application Insights
      if (appInsights) {
        const duration = Date.now() - startTime;
        appInsights.trackException({
          exception: error instanceof Error ? error : new Error('Unknown error in semantic analysis'),
          properties: {
            correlationId,
            workflowId: request.workflow_id,
            clientId: request.client_id,
            operation: 'startAnalysis',
            durationMs: duration,
            errorType: error instanceof Error ? error.name : typeof error,
            errorMessage: error instanceof Error ? error.message : String(error),
            timestamp: new Date().toISOString()
          }
        });
      }
      
      throw error;
    }
  }

  // Get analysis status by analysis ID
  public async getAnalysisStatus(analysisId: string, clientId: string, workflowId: string): Promise<SemanticAnalysisStatus> {
    console.log('Getting analysis status for ID:', analysisId);
    
    try {
      const response = await semanticApi.get(`/api/v1/semantic/${analysisId}`, {
        params: {
          client_id: clientId,
          workflow_id: workflowId
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error getting analysis status:', error);
      throw error;
    }
  }

  // Get analysis status by workflow ID
  public async getAnalysisByWorkflow(workflowId: string, clientId: string): Promise<SemanticAnalysisStatus> {
    console.log('Getting analysis for workflow:', workflowId);
    
    try {
      const response = await semanticApi.get(`/api/v1/semantic/workflow/${workflowId}`, {
        params: { client_id: clientId }
      });
      return response.data;
    } catch (error) {
      console.error('Error getting analysis by workflow:', error);
      throw error;
    }
  }

  // Get framework by ID
  public async getFramework(frameworkId: string): Promise<SemanticFramework> {
    console.log('Getting semantic framework:', frameworkId);
    
    try {
      // This would typically be a direct API call to get the framework by ID
      // For now, we'll use the blob service to get it from storage
      const response = await semanticApi.get(`/api/v1/semantic/framework/${frameworkId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting semantic framework:', error);
      // Return an empty framework structure in case of error
      return {
        framework_id: '',
        workflow_id: '',
        client_id: '',
        categories: [],
        created_at: new Date().toISOString(),
        version: 1,
        is_finalized: false
      };
    }
  }

  // Export framework to Module 4 configuration
  public async exportToModule4(request: ExportModule4Request): Promise<any> {
    console.log('Exporting framework to Module 4:', JSON.stringify(request, null, 2));
    
    // Track API call in Application Insights
    const appInsights = this.getAppInsights();
    const startTime = Date.now();
    const correlationId = `export-module4-${request.framework_id}-${Date.now()}`;
    
    if (appInsights) {
      appInsights.trackEvent({
        name: 'SemanticAPI_ExportToModule4Started',
        properties: {
          correlationId,
          frameworkId: request.framework_id,
          clientId: request.client_id,
          workflowId: request.workflow_id,
          operation: 'exportToModule4',
          timestamp: new Date().toISOString()
        }
      });
    }
    
    try {
      // Create a safe copy of the request object without any potential DOM references
      const sanitizedRequest = {
        framework_id: request.framework_id,
        client_id: request.client_id,
        workflow_id: request.workflow_id,
        config: request.config ? {
          config_id: request.config.config_id,
          name: request.config.name,
          description: request.config.description
        } : undefined
      };
      
      const response = await semanticApi.post('/api/v1/semantic/export-module4', sanitizedRequest);
      
      // If we have config data in the response, improve the system prompt
      if (response.data && response.data.prompt && response.data.prompt.system_prompt) {
        // Replace the beginning of the system prompt with a more generic expert introduction
        const originalPrompt = response.data.prompt.system_prompt;
        
        // Find where to split the prompt - after "You are an expert in categorizing" and before "Analyze the query"
        const expertPart = originalPrompt.indexOf("You are an expert in categorizing");
        let analyzeQueryPart = originalPrompt.indexOf("Analyze the query");
        
        // If we can find both parts, replace the text between them
        if (expertPart !== -1 && analyzeQueryPart !== -1) {
          // Get the text before "You are an expert"
          const beforeExpert = originalPrompt.substring(0, expertPart);
          
          // Get the text from "Analyze the query" onwards
          const fromAnalyzeQuery = originalPrompt.substring(analyzeQueryPart);
          
          // Create improved prompt with generic expert introduction
          response.data.prompt.system_prompt = 
            beforeExpert + 
            "You are an expert in categorizing search keywords based on user intent and semantic patterns. " + 
            fromAnalyzeQuery;
        }
      }
      
      // Track successful API call in Application Insights
      if (appInsights) {
        const duration = Date.now() - startTime;
        appInsights.trackEvent({
          name: 'SemanticAPI_ExportToModule4Completed',
          properties: {
            correlationId,
            frameworkId: request.framework_id,
            operation: 'exportToModule4',
            durationMs: duration,
            timestamp: new Date().toISOString()
          }
        });
      }
      
      return response.data;
    } catch (error: any) {
      console.error('Error exporting to Module 4:', error);
      
      // Log detailed error information
      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
        console.error('Response headers:', error.response.headers);
      }
      
      // Track API error in Application Insights
      if (appInsights) {
        const duration = Date.now() - startTime;
        appInsights.trackException({
          exception: error instanceof Error ? error : new Error('Unknown error in Module 4 export'),
          properties: {
            correlationId,
            frameworkId: request.framework_id,
            clientId: request.client_id,
            workflowId: request.workflow_id,
            operation: 'exportToModule4',
            durationMs: duration,
            errorType: error instanceof Error ? error.name : typeof error,
            errorMessage: error instanceof Error ? error.message : String(error),
            responseStatus: error.response?.status,
            responseData: JSON.stringify(error.response?.data),
            timestamp: new Date().toISOString()
          }
        });
      }
      
      throw error;
    }
  }

  // Helper method to convert semantic framework to classification framework format
  // This is needed for backward compatibility with existing UI components
  public convertToClassificationFramework(framework: SemanticFramework): any {
    if (!framework || !framework.categories) {
      return null;
    }
    // Convert categories array to categories array
    const categories = framework.categories.map(category => ({
      name: category.name,
      definition: category.definition,
      examples: category.examples || []
    }));
    
    return {
      id: framework.framework_id || 'generated',
      name: 'Semantic Classification Framework',
      description: 'Generated from semantic analysis',
      categories,
      created_at: framework.created_at,
      version: framework.version || 1
    };
  }
}

// React Query hooks

// Start semantic analysis
export const useStartSemanticAnalysis = () => {
  return useMutation({
    mutationFn: (request: SemanticAnalysisRequest) => 
      SemanticService.getInstance().startAnalysis(request),
  });
};

// Get analysis status by ID
export const useGetSemanticAnalysisStatus = (analysisId: string, clientId: string, workflowId: string) => {
  return useQuery({
    queryKey: ['semanticAnalysisStatus', analysisId, clientId, workflowId],
    queryFn: () => SemanticService.getInstance().getAnalysisStatus(analysisId, clientId, workflowId),
    enabled: !!analysisId && !!clientId && !!workflowId,
    refetchInterval: 5000 // Poll every 5 seconds
  });
};

// Get analysis by workflow ID
export const useGetSemanticAnalysisByWorkflow = (workflowId: string, clientId: string) => {
  return useQuery({
    queryKey: ['semanticAnalysis', 'workflow', workflowId, clientId],
    queryFn: () => SemanticService.getInstance().getAnalysisByWorkflow(workflowId, clientId),
    enabled: !!workflowId && !!clientId,
    refetchInterval: 5000 // Poll every 5 seconds
  });
};

// Get framework by ID
export const useGetSemanticFramework = (frameworkId: string) => {
  return useQuery({
    queryKey: ['semanticFramework', frameworkId],
    queryFn: () => SemanticService.getInstance().getFramework(frameworkId),
    enabled: !!frameworkId
  });
};

// Export to Module 4
export const useExportToModule4 = () => {
  return useMutation({
    mutationFn: (request: ExportModule4Request) => 
      SemanticService.getInstance().exportToModule4(request),
  });
};

// Singleton instance export
export const semanticService = SemanticService.getInstance();

export default semanticService;
